import { Heading3, P, Ul, Li, Strong } from '@ovotech/nebula';
import { AnalyticsRender, useAnalytics } from '@ovotech/ui-tools';
import React from 'react';
import Offer from '../Offer/Offer';

import { HOMEPLAN_DEFAULT_CARD } from '@/src/constants/analytics-subjects';
import { createHomePlanUrl } from '@/src/constants/endpoints';
import image from '@/src/resources/offers/boiler-cover.jpg';
import { useHomeplanOffer } from '@/src/hooks/HomeServices/useHomeplanOffer';
import styled from 'styled-components';
import { FootnoteReferenced } from '@/src/components';
import { getOfferFootnote } from '../../FooterTermsAndConditions';
import { dateToDayMonthYear } from '@/src/utils/date';
import { mqMediumUp } from '@/src/utils/mediaQuery';
import { formatDiscountPercentage } from './formatUtils';

type Props = {
  accountId: string;
};

const Roundel = styled.div`
  ${({ theme: { colors } }) => `
  width: 115px;
  height: 115px;
  position: absolute;
  font-weight: 700;
  right: 5%;
  top: -28%;
  line-height:16px;
  border-radius: 50%;
  background-color: ${colors.green.energised};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  color: ${colors.green.midnight};
 
    ${mqMediumUp(`
      
      top: -20%;
    `)}
  `}
`;

const FreeMonthNumber = styled.h2`
  font-size: 42px;
  font-weight: 700;
  line-height: 42px;
  text-align: center;
`;

const FreeStong = styled(Strong)`
  font-weight: 900;
`;

export const HomePlanPromoOffer = ({ accountId }: Props) => {
  const analytics = useAnalytics();
  const {
    promoEndDate,
    isOfferAvailable,
    promotionDuration,
    isTieredOfferDuration,
    isTieredOfferDiscount,
    offerType,
    maxDiscountPercentage,
  } = useHomeplanOffer();

  const friendlyDateFormat =
    promoEndDate && dateToDayMonthYear(new Date(promoEndDate));
  return (
    <AnalyticsRender name={HOMEPLAN_DEFAULT_CARD}>
      <Offer
        image={image}
        offerId="homePlanPromo"
        linkUrl={createHomePlanUrl('homePlanPromo', accountId)}
        linkText="Get cover"
        tracking={() => {
          analytics.dispatch({
            name: HOMEPLAN_DEFAULT_CARD,
            type: 'click',
          });
        }}
      >
        {isOfferAvailable && offerType === 'duration' && (
          <>
            <Heading3>
              Get {isTieredOfferDuration && 'up to '}
              {promotionDuration} months{' '}
              {formatDiscountPercentage(maxDiscountPercentage)} on boiler cover{' '}
              <FootnoteReferenced
                content={getOfferFootnote(friendlyDateFormat)}
              />
            </Heading3>

            <Roundel>
              <span>{isTieredOfferDuration && 'Up to '}</span>
              <FreeMonthNumber>{promotionDuration}</FreeMonthNumber>
              <span>
                months
                <br />
                <span>
                  <FreeStong>
                    {formatDiscountPercentage(maxDiscountPercentage)}
                  </FreeStong>
                </span>
              </span>
            </Roundel>
          </>
        )}

        {isOfferAvailable && offerType === 'discount' && (
          <>
            <Heading3>
              Get {isTieredOfferDiscount && 'up to '}
              {maxDiscountPercentage}% off on boiler cover{' '}
              <FootnoteReferenced
                content={getOfferFootnote(friendlyDateFormat)}
              />
            </Heading3>

            <Roundel>
              <span>{isTieredOfferDiscount && 'Up to '}</span>
              <FreeMonthNumber>{maxDiscountPercentage}%</FreeMonthNumber>
              <span>off</span>
            </Roundel>
          </>
        )}

        {!isOfferAvailable && (
          <Heading3 as="h2">Boiler cover from OVO.</Heading3>
        )}

        <P>
          We helped with over 120,000 repairs last year, so you know you can
          count on us. All our plans come with:
        </P>

        <Ul>
          <Li>An annual boiler service worth £84</Li>
          <Li>Unlimited callouts</Li>
          <Li>A 24/7 emergency helpline</Li>
          <Li>Access to a network of Gas Safe registered engineers</Li>
        </Ul>
      </Offer>
    </AnalyticsRender>
  );
};
